import { fmtChange, pctChange, type PairedRow } from '../utils';

/*
 * Building blocks of the Deel 1 vs Deel 2 comparison (design "Deel 2 wk 1"):
 * the colour legend, paired bars (Deel 1 muted above Deel 2 in AH blue) and a
 * two-value stat with its change. Deel 1 is always the muted one — it is the
 * reference, Deel 2 is what is being watched.
 */

export type PartLabels = { previous: string; current: string };

export function PartLegend({ labels }: { labels: PartLabels }) {
  return (
    <div className="pf-legend">
      <span>
        {labels.previous}
        <span className="pf-swatch pf-d1" />
      </span>
      <span>
        {labels.current}
        <span className="pf-swatch pf-d2" />
      </span>
    </div>
  );
}

/**
 * One row per label, both parts' bars stacked, both values on the right.
 * `scale` is the value a full bar stands for: 100 for shares, the largest value
 * for counts, so the two parts are drawn against the SAME axis.
 */
export function PairedBars({
  rows,
  format,
  scale,
  short,
}: {
  rows: PairedRow[];
  format: (n: number) => string;
  scale: number;
  short: { previous: string; current: string };
}) {
  const width = (n: number) => `${scale > 0 ? Math.min(100, (n / scale) * 100) : 0}%`;
  return (
    <div className="pf-paired">
      <div className="pf-paired-row pf-paired-head" aria-hidden="true">
        <span />
        <span />
        <span className="pf-v pf-v-d1">{short.previous}</span>
        <span className="pf-v pf-v-d2">{short.current}</span>
      </div>
      {rows.map((r) => (
        <div className="pf-paired-row" key={r.label}>
          <span className="pf-paired-label">{r.label}</span>
          <div className="pf-paired-bars">
            <div className="pf-track">
              <div className="pf-fill pf-d1" style={{ width: width(r.previous) }} />
            </div>
            <div className="pf-track">
              <div className="pf-fill pf-d2" style={{ width: width(r.current) }} />
            </div>
          </div>
          <span className="pf-v pf-v-d1">{format(r.previous)}</span>
          <span className="pf-v pf-v-d2">{format(r.current)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * A per-user figure for both parts with the change between them. `prevValue` /
 * `curValue` are the comparable numbers; the display strings are the pipeline's.
 */
export function CompareStat({
  label,
  labels,
  previous,
  current,
  prevValue,
  curValue,
  desc,
}: {
  label: string;
  labels: PartLabels;
  previous: string;
  current: string;
  prevValue: number | null;
  curValue: number | null;
  desc: string;
}) {
  const change = pctChange(prevValue, curValue);
  return (
    <div className="pf-compare-stat">
      <div className="pf-compare-top">
        <span className="pf-compare-label">{label}</span>
        {change != null && <span className={`pf-change ${change < 0 ? 'down' : change > 0 ? 'up' : ''}`}>{fmtChange(change)}</span>}
      </div>
      <div className="pf-compare-values">
        <div>
          <div className="k">{labels.previous}</div>
          <div className="v pf-muted">{previous}</div>
        </div>
        <div>
          <div className="k pf-k-d2">{labels.current}</div>
          <div className="v">{current}</div>
        </div>
      </div>
      <div className="pf-compare-desc">{desc}</div>
    </div>
  );
}
